import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";

// Placeholder explainer video - swap for the real one when it's ready.
const HOW_IT_WORKS_VIDEO =
  "https://www.loom.com/share/f05c43e6362f467cb873d665b451e772";

/**
 * "(?)" trigger beside the Start Flexible Program button - hovering reveals
 * a popover with a short explainer video and a "How it works" blurb. Loom's
 * oEmbed HTML already ships its own thumbnail/play-button overlay (same
 * dangerouslySetInnerHTML approach as WeekVideo.js), so nothing custom is
 * needed there.
 */
const FlexibleHowItWorks = () => {
  const { t } = useTranslation("athlete-portal");
  const [videoHTML, setVideoHTML] = useState(null);
  const [error, setError] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    import("@loomhq/loom-embed")
      .then(({ oembed }) => oembed(HOW_IT_WORKS_VIDEO))
      .then((res) => {
        if (!cancelled) setVideoHTML(res.html);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The popover is pure CSS :hover, but going fullscreen on the embedded
  // player can leave the cursor no longer registering as "over" the trigger
  // mid-transition - :hover then drops, the popover's ancestor goes
  // invisible, and the fullscreened video (still a descendant of it) goes
  // invisible too even though it keeps playing. Forcing the popover open for
  // as long as fullscreen is active on something inside it sidesteps that
  // entirely, regardless of what the cursor is doing.
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElement = document.fullscreenElement;
      setFullscreenOpen(
        !!fsElement && !!wrapperRef.current?.contains(fsElement)
      );
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <span
      className={`flexible-how-it-works${fullscreenOpen ? " flexible-how-it-works-force-open" : ""}`}
      ref={wrapperRef}
    >
      <SvgIcon
        src="/img/new-icons/question-circle.svg"
        alt={t("flexible.infoIconAlt")}
      />
      <div className="flexible-how-it-works-popover" role="tooltip">
        <div className="flexible-how-it-works-popover-card">
          <div className="flexible-how-it-works-video">
            {error ? (
              <p className="s4">{t("flexible.howItWorksVideoError")}</p>
            ) : (
              videoHTML && (
                <div dangerouslySetInnerHTML={{ __html: videoHTML }} />
              )
            )}
          </div>
          <p className="flexible-how-it-works-title">
            {t("flexible.howItWorksTitle")}
          </p>
          <p className="flexible-how-it-works-body">
            {t("flexible.howItWorksBody")}
          </p>
        </div>
      </div>
    </span>
  );
};

export default FlexibleHowItWorks;
